import { Booking } from './types';

/** Statuses whose booking no longer holds its object, whatever its end date. */
const RELEASED_STATUSES = new Set(['canceled', 'finished', 'expired']);

/**
 * Archiving hides a booking from the default list; it does not cancel it. A
 * booking that is neither cancelled nor ended still holds its object for its
 * window, archived or not, a paid booking and an unpaid reservation alike.
 * (A reservation's payment deadline does not cancel it either yet: the
 * `onExpiry` wires are unbuilt, see docs/plans/service-configuration-area.md.)
 * Archiving replaced bookings instead of cancelling them is how they came to
 * overlap their replacements on a lead customer's tenant (2026-10-13).
 *
 * Client-side reading of the backend's "does this order still occupy?" rule:
 * an order counts unless `is_canceled` is true or it has an inactive rental
 * item (cancelled or finished early) — `assertCapacityAvailableTx`
 * (backend groupService.ts) and `OCCUPANCY_ITEMS_SQL` (availability
 * occupancy.ts). The bookings list folds both into `bookingStatus`, so
 * `canceled` and `finished` release the object here; `expired` has a past end
 * by definition and is listed for completeness.
 *
 * True when the archive dialog should say so: such a status is absent and the
 * end lies in the future. A past booking blocks nothing any more.
 */
export const archiveKeepsOccupying = (
    booking: Pick<Booking, 'bookingStatus' | 'endDate'>,
    now: Date = new Date(),
): boolean => {
    if (RELEASED_STATUSES.has(booking.bookingStatus?.toLowerCase())) return false;
    const end = new Date(booking.endDate).getTime();
    return Number.isFinite(end) && end > now.getTime();
};
